/**
 * 엑셀에서 열리는 CSV 내보내기.
 *
 * 외부 라이브러리를 쓰지 않는다. xlsx 생성기를 넣으면 파일 크기가 몇 배로
 * 늘어 단일 HTML 파일 배포가 곤란해진다(CLAUDE.md). CSV 는 엑셀이 바로 열고
 * 검토자가 숫자를 그대로 만질 수 있어 목적에 충분하다.
 *
 * 한글이 깨지지 않도록 **UTF-8 BOM** 을 붙인다 — 이게 없으면 엑셀이
 * 시스템 인코딩으로 읽어 한글이 깨진다.
 */
import type {
  GoverningCaseResult,
  PsvCase,
  SummaryMatrix,
} from "../calc/governingCase";
import { findScenario } from "../data/scenarios";
import { sizingSheetRows, fluidLabel } from "./sheetRows";
import { sizingFormulas } from "./formulas";
import { runPiping, type PipingState } from "./pipingState";
import {
  compareScenarios,
  runFlare,
  scenarioFlow,
  type FlareState,
} from "./flareState";
import { formatFromKpa, gaugeLabel, type PressureUnit } from "./pressureUnit";
import { outputPointsOf, type FlowsheetNodeForm } from "./flowsheetState";
import type { ProcessNetworkResult } from "../calc/processNetwork";

export interface CsvSource {
  readonly psvCase: PsvCase;
  readonly result: GoverningCaseResult;
}

/** CSV 한 칸. 쉼표·따옴표·줄바꿈이 있으면 감싸고 따옴표는 두 번 쓴다. */
function cell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function row(...cells: Array<string | number | null | undefined>): string {
  return cells.map(cell).join(",");
}

/** 여러 밸브의 검토 결과를 하나의 CSV 문자열로 만든다. */
export function buildCsv(sources: readonly CsvSource[]): string {
  const lines: string[] = [];

  lines.push(row("안전밸브 사이징 계산서"));
  lines.push(
    row(
      "근거",
      "API 520 Part I 10판(2020) / API 521(2020) / API 526 4판(1995)",
    ),
  );
  lines.push(row("작성일", new Date().toLocaleDateString("ko-KR")));
  lines.push("");

  for (const { psvCase, result } of sources) {
    lines.push(row(`■ ${psvCase.tag}`));
    lines.push(
      row(
        "설정압(kPag)",
        psvCase.setPressureKpaGauge,
        "설치 형태",
        psvCase.installation === "single" ? "단일 설치" : "복수 설치",
      ),
    );
    lines.push("");

    lines.push(row("[과압 시나리오 검토]"));
    lines.push(
      row(
        "No.",
        "시나리오",
        "조항",
        "상황 설명",
        "유체",
        "분출용량",
        "단위",
        "과압(%)",
        "P1(kPa abs)",
        "소요면적(mm2)",
        "판정",
      ),
    );
    for (const e of result.ranked) {
      const def = findScenario(e.entry.item);
      lines.push(
        row(
          e.entry.item,
          def?.conditionKo ?? e.entry.label,
          def?.section,
          e.entry.description,
          e.detail ? fluidLabel(e.detail.kind) : "",
          e.flow.value,
          e.flow.unit,
          e.overpressurePercent,
          e.relievingPressureKpaAbs.toFixed(1),
          e.requiredAreaMm2 === null ? "" : e.requiredAreaMm2.toFixed(1),
          result.governing?.entry.id === e.entry.id
            ? "지배"
            : e.error
              ? `계산 불가: ${e.error}`
              : "",
        ),
      );
    }
    lines.push("");

    // 시나리오마다 계산 과정을 싣는다. 지배 케이스만 실으면 검토자가
    // 채택되지 않은 케이스를 확인할 수 없다.
    lines.push(row("[시나리오별 계산 과정]"));
    for (const e of result.ranked) {
      if (e.detail === null) continue;
      const def = findScenario(e.entry.item);
      lines.push(
        row(
          `${e.entry.item}. ${def?.conditionKo ?? e.entry.label}`,
          result.governing?.entry.id === e.entry.id ? "지배 케이스" : "",
        ),
      );
      lines.push(row("구하는 값", "근거", "식", "대입", "결과"));
      for (const f of sizingFormulas(e.detail)) {
        lines.push(
          row(f.label, f.source, f.symbolic, f.substituted ?? "", f.result),
        );
      }
      lines.push("");
    }

    const gov = result.governing;
    if (gov) {
      lines.push(row("[지배 케이스 적용 계수]"));
      lines.push(
        row("시나리오", `${gov.entry.item}. ${gov.entry.label}`),
        row("적용 식", gov.equation),
        row("항목", "값", "근거"),
      );
      if (gov.detail) {
        for (const r of sizingSheetRows(gov.detail)) {
          lines.push(row(r.label, r.value, r.source));
        }
      }
      lines.push(
        row("소요 유효 오리피스 면적", `${gov.requiredAreaMm2!.toFixed(1)} mm2`, gov.equation),
      );
      lines.push("");

      const sel = result.selection;
      lines.push(row("[밸브 선정]"));
      if (sel?.ok) {
        lines.push(
          row(
            "선정 오리피스",
            sel.orifice.designation +
              (sel.valveCount > 1 ? ` x ${sel.valveCount}개 병렬` : ""),
          ),
          row("유효 면적(mm2)", sel.orifice.effectiveAreaMm2.toFixed(2)),
          row("유효 면적(in2)", sel.orifice.effectiveAreaIn2),
          row("여유율(%)", sel.marginPct.toFixed(1)),
          row("제공 규격", "입구", "출구", "입구 클래스", "출구 클래스"),
        );
        for (const v of sel.orifice.valveSizes) {
          lines.push(
            row(v.size, v.inletNps, v.outletNps, v.inletClass, v.outletClass),
          );
        }
      } else {
        lines.push(row("선정 불가", sel?.message ?? ""));
      }
    } else {
      lines.push(row("지배 케이스를 정할 수 없습니다."));
    }

    const warnings = result.ranked.flatMap((e) =>
      e.warnings.map((w) => ({ e, w })),
    );
    if (warnings.length > 0) {
      lines.push("");
      lines.push(row("[확인 필요 사항]"));
      for (const { e, w } of warnings) {
        lines.push(row(`${e.entry.item}. ${e.entry.label}`, w));
      }
    }

    lines.push("");
    lines.push("");
  }

  lines.push(
    row(
      "비고",
      "본 계산서는 예비 사이징(effective area, effective Kd) 기준입니다. 최종 선정은 제작사의 인증 면적/인증 배출계수로 재확인해야 합니다 (API 520 Part I 5.2.4).",
    ),
  );

  // 엑셀은 CRLF 를 기대한다.
  return lines.join("\r\n");
}

/**
 * 검토 요약 매트릭스를 CSV 로 만든다.
 * 행 = API 521 시나리오 17종, 열 = PSV 태그.
 *
 * 해당 시나리오가 없는 칸은 `N/A` 로 남긴다 — 빈칸으로 두면 엑셀에서
 * "빠뜨린 것"과 "해당 없는 것"을 구분할 수 없다.
 */
export function buildSummaryCsv(
  matrix: SummaryMatrix,
  metric: "flow" | "mass" | "area",
): string {
  const lines: string[] = [];

  lines.push(row("검토 요약 — 밸브별 시나리오 비교"));
  lines.push(
    row(
      "표시값",
      metric === "flow"
        ? "소요 분출용량"
        : metric === "mass"
          ? "질량유량(PSM 기재용, kg/h)"
          : "소요 면적(mm2)",
    ),
  );
  lines.push(
    row("행", "API 521 Table 1 과압 시나리오 17종 (N/A = 해당 없음)"),
  );
  lines.push(row("작성일", new Date().toLocaleDateString("ko-KR")));
  lines.push("");

  const tags = matrix.columns.map((c) => c.psvCase.tag);
  lines.push(row("No.", "시나리오", "조항", ...tags));

  for (const r of matrix.rows) {
    const def = findScenario(r.item);
    const cells = r.cells.map((c) => {
      if (c.kind === "na") return "N/A";
      if (c.kind === "error") return "계산 불가";
      if (metric === "flow") {
        return c.flow === null ? "" : `${c.flow.value} ${c.flow.unit}`;
      }
      if (metric === "mass") {
        return c.flow === null ? "" : c.flow.massFlowKgH.toFixed(1);
      }
      return c.requiredAreaMm2 === null ? "" : c.requiredAreaMm2.toFixed(1);
    });
    lines.push(row(r.item, def?.conditionKo, def?.section, ...cells));
  }

  lines.push("");
  lines.push(
    row(
      "지배 케이스",
      "",
      "",
      ...matrix.columns.map((c) =>
        c.result.governing
          ? `${c.result.governing.entry.item}. ${c.result.governing.entry.label}`
          : "—",
      ),
    ),
  );
  lines.push(
    row(
      "선정 오리피스",
      "",
      "",
      ...matrix.columns.map((c) =>
        c.result.selection?.ok
          ? c.result.selection.orifice.designation +
            (c.result.selection.valveCount > 1
              ? ` x ${c.result.selection.valveCount}`
              : "")
          : "—",
      ),
    ),
  );
  lines.push(
    row(
      "설정압(kPag)",
      "",
      "",
      ...matrix.columns.map((c) => c.psvCase.setPressureKpaGauge),
    ),
  );

  lines.push("");
  lines.push("");
  lines.push(row("[과압 상황 설명]"));
  lines.push(row("밸브", "No.", "시나리오", "상황 설명"));
  for (const col of matrix.columns) {
    for (const s of col.psvCase.scenarios) {
      lines.push(row(col.psvCase.tag, s.item, s.label, s.description));
    }
  }

  return lines.join("\r\n");
}

/* ── 배관 검토·플레어 헤더 ────────────────────────────────
 * 계산서에 실을 항목을 고르게 했으니 CSV 도 같이 따라가야 한다.
 * 고르지 않은 항목은 아예 넣지 않는다 — 빈 표가 붙으면 검토자가
 * "계산을 안 한 것"과 "0 이 나온 것"을 구분하지 못한다.
 */

/** 배관 검토 결과를 CSV 줄로. */
export function pipingCsvLines(
  state: PipingState,
  unit: PressureUnit,
): string[] {
  const out = runPiping(state, unit);
  const lines: string[] = [];
  const g = gaugeLabel(unit);

  lines.push(row("■ 배관 검토 계산서"));
  lines.push(row("근거", "API 520 Part II 6판(2015) 7.3 / 5.3"));
  lines.push(row("태그", state.tag));
  lines.push(row("설정압", state.setPressure, g, "MAWP", state.mawp, g));
  lines.push(
    row(
      "허용 축적 (%)",
      state.allowableAccumulationPercent,
      "밸브 형식",
      // 내부 코드가 아니라 화면과 같은 표기를 쓴다 — 검토자가 받는 파일이다.
      state.valveType === "conventional"
        ? "일반형"
        : state.valveType === "balanced-bellows"
          ? "밸런스드 벨로우즈"
          : "파일럿형",
    ),
  );
  lines.push(
    row(
      "유량 (kg/h)",
      state.flowKgH,
      "기준",
      state.flowBasis === "rated" ? "정격 용량" : "소요 유량",
    ),
  );
  lines.push(
    row("밀도 (kg/m³)", state.densityKgM3, "점도 (cP)", state.viscosityCp),
  );
  lines.push(
    row(
      "손실 계산 방식",
      state.method === "equivalent-length" ? "등가길이 (L/D)" : "저항계수 (K)",
    ),
  );
  lines.push("");

  if (!out.ok) {
    lines.push(row("계산 불가", out.message ?? ""));
    lines.push("");
    return lines;
  }

  for (const [title, run] of [
    ["입구 배관", out.inlet],
    ["출구 배관", out.outlet],
  ] as const) {
    if (run === null) continue;
    lines.push(row(title));
    lines.push(row("항목", "값", "단위"));
    lines.push(row("내경", run.innerDiameterMm.toFixed(2), "mm"));
    lines.push(row("계산 길이", run.totalLengthM.toFixed(2), "m"));
    lines.push(row("부속 등가길이", run.equivalentLengthM.toFixed(2), "m"));
    lines.push(row("ΣK", run.fittingK.toFixed(2), "—"));
    lines.push(
      row(
        run.compressible === null ? "유속" : "유속 (출구단)",
        run.velocityMs.toFixed(2),
        "m/s",
      ),
    );
    lines.push(row("레이놀즈수", run.reynolds.toExponential(3), "—"));
    lines.push(row("마찰계수 f", run.frictionFactor.toFixed(4), "—"));
    lines.push(row("압력손실", run.dropKpa.toFixed(2), "kPa"));
    lines.push(row("  길이 몫", run.frictionKpa.toFixed(2), "kPa"));
    lines.push(row("  SigmaK 몫", run.fittingKpa.toFixed(2), "kPa"));
    if (run.compressible !== null) {
      const c = run.compressible;
      lines.push(row("계산 방식", "등온 압축성 (KOSHA D-63 4.1(1))", "—"));
      lines.push(row("출구단 마하수", c.outletMach.toFixed(3), "—"));
      lines.push(row("입구단 마하수", c.inletMach.toFixed(3), "—"));
      lines.push(row("출구단 밀도", c.outletDensityKgM3.toFixed(3), "kg/m3"));
      lines.push(row("관 입구 절대압", c.inletPressureKpaAbs.toFixed(2), "kPa abs"));
      lines.push(row("관 출구 절대압", c.outletPressureKpaAbs.toFixed(2), "kPa abs"));
      lines.push(row("초크 한계압", c.criticalPressureKpaAbs.toFixed(2), "kPa abs"));
    } else if (title === "출구 배관") {
      lines.push(row("계산 방식", "비압축성 (Darcy-Weisbach)", "—"));
    }
    lines.push("");
  }

  if (out.inletCheck?.ok) {
    lines.push(row("입구 3 % 판정 (7.3.4)"));
    lines.push(row("배관 손실", out.inletCheck.pipingLossKpa.toFixed(2), "kPa"));
    lines.push(
      row("파열판 몫", out.inletCheck.ruptureDiskLossKpa.toFixed(2), "kPa"),
    );
    lines.push(row("총 손실", out.inletCheck.totalLossKpa.toFixed(2), "kPa"));
    lines.push(row("설정압 대비", out.inletCheck.lossPercent.toFixed(2), "%"));
    lines.push(row("판정", out.inletCheck.withinLimit ? "적합" : "부적합"));
    lines.push("");
  }

  if (out.forceBalance) {
    lines.push(row("힘 평형 대안 판정 (7.3.6 d)"));
    lines.push(row("기준", out.forceBalance.criterion));
    lines.push(row("좌변", out.forceBalance.leftSide.toFixed(2), "kPa"));
    lines.push(row("우변", out.forceBalance.rightSide.toFixed(2), "kPa"));
    lines.push(row("판정", out.forceBalance.passes ? "만족" : "불만족"));
    lines.push("");
  }

  if (out.backpressure?.ok) {
    lines.push(row("배압 판정 (5.3)"));
    lines.push(row("기준", out.backpressure.criterion));
    lines.push(row("중첩배압", state.superimposed, g));
    lines.push(row("축적배압", (out.outlet?.dropKpa ?? 0).toFixed(2), "kPa"));
    lines.push(
      row("총 배압", out.backpressure.totalKpaGauge.toFixed(2), "kPag"),
    );
    lines.push(
      row(
        "허용 축적배압",
        out.backpressure.allowableBuiltUpKpa === null
          ? "형식별 다른 기준"
          : out.backpressure.allowableBuiltUpKpa.toFixed(2),
        "kPa",
      ),
    );
    lines.push(
      row(
        "판정",
        out.backpressure.builtUpWithinLimit && out.backpressure.totalWithinLimit
          ? "적합"
          : "부적합",
      ),
    );
    lines.push("");
  }

  if (out.reaction?.ok) {
    lines.push(row("대기 방출 반력 (식 1)"));
    lines.push(row("운동량 몫", out.reaction.momentumN.toFixed(0), "N"));
    lines.push(row("정압 몫", out.reaction.staticN.toFixed(0), "N"));
    lines.push(row("반력 F", out.reaction.forceN.toFixed(0), "N"));
    lines.push("");
  }

  return lines;
}

/** 플레어 헤더 결과를 CSV 줄로. 조합 비교표가 핵심이다. */
export function flareCsvLines(state: FlareState, unit: PressureUnit): string[] {
  const comparison = compareScenarios(state, unit);
  const lines: string[] = [];
  const labelOf = (id: string | null) =>
    id === null
      ? "플레어 팁"
      : (state.segments.find((s) => s.id === id)?.label ?? "?");

  lines.push(row("■ 플레어 헤더 배압 계산서"));
  lines.push(row("근거", "API 521 (2020) 5.5 · 등온 압축성 유동"));
  lines.push(row("플레어 팁 압력", state.tipPressure, gaugeLabel(unit)));
  lines.push(row("마하수 상한", state.machLimit, "설계자 지정 (5.5.3)"));
  lines.push("");

  lines.push(row("헤더 구성"));
  lines.push(row("구간", "흘러가는 곳", "관경", "스케줄", "직관 길이 (m)"));
  for (const s of state.segments) {
    lines.push(
      row(
        s.label,
        labelOf(s.downstreamId),
        s.pipe.useCustomId
          ? `내경 ${s.pipe.customIdMm} mm`
          : `${s.pipe.nps} in`,
        s.pipe.useCustomId ? "" : s.pipe.schedule,
        s.pipe.straightLengthM,
      ),
    );
  }
  lines.push("");

  lines.push(row("동시 분출 조합 비교"));
  lines.push(
    row(
      "밸브",
      ...state.scenarios.flatMap((sc) => [
        `${sc.name} 유량 (kg/h)`,
        `${sc.name} 배압 (kPag)`,
      ]),
      "최악 배압 (kPag)",
      "지배 조합",
    ),
  );
  for (const v of state.valves) {
    const worst = comparison.worstPerValve.find((w) => w.valveId === v.id);
    lines.push(
      row(
        v.tag,
        ...state.scenarios.flatMap((sc) => {
          const sum = comparison.summaries.find((x) => x.scenarioId === sc.id);
          const bp = sum?.backpressures.get(v.id);
          return [scenarioFlow(sc, v.id), bp === undefined ? "" : bp.toFixed(1)];
        }),
        worst ? worst.backpressureKpaGauge.toFixed(1) : "",
        worst?.scenarioName ?? "",
      ),
    );
  }
  lines.push("");

  for (const sc of state.scenarios) {
    const { result } = runFlare(state, unit, sc.id);
    lines.push(row(`조합: ${sc.name}`));
    if (!result.ok) {
      lines.push(row("계산 불가", result.message));
      lines.push("");
      continue;
    }
    lines.push(
      row(
        "구간",
        "누적 유량 (kg/h)",
        "내경 (mm)",
        "입구 (kPa abs)",
        "출구 (kPa abs)",
        "강하 (kPa)",
        "출구 Ma",
        "임계압 (kPa abs)",
        "Re",
        "마찰계수 f",
        "혼합 M",
        "혼합 T (K)",
        "혼합 점도 (cP)",
      ),
    );
    for (const s of result.segments) {
      lines.push(
        row(
          s.label,
          s.massFlowKgH.toFixed(0),
          (s.innerDiameterM * 1000).toFixed(1),
          s.inletPressureKpaAbs.toFixed(1),
          s.outletPressureKpaAbs.toFixed(1),
          s.pressureDropKpa.toFixed(1),
          s.outletMach.toFixed(3),
          s.criticalPressureKpaAbs.toFixed(1),
          s.reynolds.toExponential(3),
          s.frictionFactor.toFixed(4),
          s.molecularWeight.toFixed(1),
          s.temperatureK.toFixed(0),
          s.viscosityCp.toFixed(4),
        ),
      );
    }
    lines.push(
      row(
        "밸브",
        "유량 (kg/h)",
        "합류점 압력 (kPa abs)",
        "테일 강하 (kPa)",
        "총 배압 (kPag)",
        "테일 Ma",
      ),
    );
    for (const v of result.valves) {
      lines.push(
        row(
          v.tag,
          v.massFlowKgH.toFixed(0),
          v.nodePressureKpaAbs.toFixed(1),
          v.tailDropKpa.toFixed(1),
          v.backpressureKpaGauge.toFixed(1),
          v.tailOutletMach.toFixed(3),
        ),
      );
    }
    lines.push("");
  }

  return lines;
}

/**
 * 계산서 탭에서 고른 항목만 담은 CSV.
 *
 * 화면의 체크박스와 CSV 가 어긋나면 검토자가 받은 파일에 무엇이 들어 있는지
 * 알 수 없다. 같은 선택을 그대로 따른다.
 */
export function buildReportCsv(opts: {
  readonly sources: readonly CsvSource[];
  readonly piping: PipingState;
  readonly flare: FlareState;
  readonly unit: PressureUnit;
  readonly include: {
    readonly sizing: boolean;
    readonly piping: boolean;
    readonly flare: boolean;
  };
}): string {
  const parts: string[] = [];
  if (opts.include.sizing) parts.push(buildCsv(opts.sources));
  if (opts.include.piping) {
    parts.push(pipingCsvLines(opts.piping, opts.unit).join("\r\n"));
  }
  if (opts.include.flare) {
    parts.push(flareCsvLines(opts.flare, opts.unit).join("\r\n"));
  }
  if (parts.length === 0) return row("실을 항목을 고르지 않았습니다.");
  return parts.join("\r\n\r\n");
}

export function defaultCsvName(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `PSV-계산서-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.csv`;
}

export function defaultSummaryCsvName(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `PSV-검토요약-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.csv`;
}

export function defaultFlowsheetCsvName(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `PSV-공정흐름도-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.csv`;
}

/**
 * 공정 흐름도의 물질·에너지수지표를 CSV 로 만든다.
 *
 * 화면 표와 같은 가로(지점을 열로, 항목을 행으로) 배치를 그대로 따른다 —
 * 화면에서 보던 표와 CSV 가 달라 보이면 검토자가 헷갈린다.
 */
export function buildFlowsheetCsv(
  result: ProcessNetworkResult,
  flowsheetNodes: readonly FlowsheetNodeForm[],
  unit: PressureUnit,
): string {
  const lines: string[] = [];
  lines.push(row("공정 흐름도 — 물질·에너지수지표"));

  if (!result.ok) {
    lines.push(row("계산할 수 없습니다", result.message, result.where));
    return lines.join("\r\n");
  }

  const points = result.nodes
    .flatMap((n) => outputPointsOf(flowsheetNodes.find((f) => f.id === n.id)!))
    .map((pt) => ({ pt, stream: result.streams.get(pt.id) }))
    .filter((x) => x.stream !== undefined);

  lines.push(row("지점", ...points.map((x) => x.pt.label)));
  lines.push(
    row("상태", ...points.map((x) => (x.stream!.phase === "gas" ? "습윤가스" : "액체"))),
  );
  lines.push(
    row(
      "건가스/액체 유량 (kg/h)",
      ...points.map((x) => {
        const s = x.stream!;
        const flow =
          s.phase === "gas"
            ? Object.values(s.components).reduce((a, b) => a + b, 0)
            : s.massFlowKgH;
        return flow.toFixed(1);
      }),
    ),
  );
  lines.push(
    row(
      "수분 유량 (kg/h)",
      ...points.map((x) =>
        x.stream!.phase === "gas" ? x.stream!.waterVaporMassFlowKgH.toFixed(1) : "",
      ),
    ),
  );
  lines.push(
    row("온도 (℃)", ...points.map((x) => (x.stream!.temperatureK - 273.15).toFixed(1))),
  );
  lines.push(
    row(
      `압력 (${gaugeLabel(unit)})`,
      ...points.map((x) =>
        formatFromKpa(x.stream!.pressureKpaAbs - 101.325, unit),
      ),
    ),
  );

  if (result.warnings.length > 0) {
    lines.push("");
    lines.push(row("경고"));
    for (const w of result.warnings) lines.push(row(w));
  }

  return lines.join("\r\n");
}

/** UTF-8 BOM 을 붙여 내려받는다. BOM 이 없으면 엑셀에서 한글이 깨진다. */
export function downloadCsv(text: string, fileName = defaultCsvName()): void {
  const blob = new Blob(["﻿" + text], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
